import moment from 'moment';
import { FeeSplit, isFeeSplitTypeFullWaiver } from '../types/feeSplit';
import {
  FirebaseTimestamp,
  MailDelivery,
  EFirebaseContext,
  ESnapshotExists,
  EOrganization,
  ENotice,
  exists
} from '../types';
import {
  NoticeAddOnPricing,
  NoticeAddOnPricingContext
} from './noticeAddOns/types';
import { MailedNoticeDocumentType } from '../types/mail';
import { getMailDataFromNoticeOrDraft } from '../mail';
import { getBillableMail } from '../mail/noticeTypeDefaultMail';
import {
  getNoticeTypeFromNoticeData,
  sortNoticePublicationDatesChronologically
} from '../helpers';
import { getCustomer, getCustomerOrganization } from '../notice/customer';
import { AffidavitReconciliationSettings } from '../types/organization';
import { AffidavitPricingData } from '.';
import { getOrThrow } from '../utils/refs';
import { AAPendingQueryNotice } from '../affidavits/types';
import { getModelFromSnapshot } from '../model';
import { UserNoticeModel } from '../model/objects/userNoticeModel';
import { getErrorReporter, isPermissionDeniedError } from '../utils/errors';
import { ColumnService } from '../services/directory';

const DEFAULT_AFFIDAVIT_FEE_IN_CENTS = 0;

/**
 * Returns the reconciliation settings affiliated with the newspaper. Goes in precendence
 * order of:
 * 1. What is set on the newspaper (if anything)
 * 2. What is set on the parent (if anything)
 * 3. Returns undefined if no settings on either
 */
export const getAffidavitSettingsForNewspaper = async (
  newspaperSnap: ESnapshotExists<EOrganization> | undefined
): Promise<AffidavitReconciliationSettings | undefined> => {
  if (!exists(newspaperSnap)) return undefined;
  if (newspaperSnap.data()?.affidavitReconciliationSettings) {
    return newspaperSnap.data()?.affidavitReconciliationSettings || undefined;
  }

  const parentSnap = await newspaperSnap.data()?.parent?.get();
  if (parentSnap?.data()?.affidavitReconciliationSettings) {
    return parentSnap?.data()?.affidavitReconciliationSettings || undefined;
  }

  return undefined;
};

/**
 * Determines the affidavit reconciliation settings for notice data. This can be used
 * in the placement flow before we have a full notice object.
 * @param {EFirebaseContext} ctx context object
 * @param {ENotice} noticeData notice data
 * @returns {Promise<AffidavitReconciliationSettings | undefined>} affidavit reconciliation settings
 */
export type NoticeWithAffidavitData = Partial<
  Pick<
    ENotice,
    | 'newspaper'
    | 'affidavitReconciliationSettings'
    | 'filer'
    | 'noticeType'
    | 'previousNoticeType'
    | 'filedBy'
  >
>;
export const getAffidavitSettingsForNoticeData = async (
  ctx: EFirebaseContext,
  noticeData: NoticeWithAffidavitData,
  options?: {
    /**
     * If true, we pull only settings from the newspaper (plus notice type or customer-specific
     * settings), ignoring any notice-level settings. This is useful when a notice gets invoiced
     * for the second time, and needs to have its settings refreshed.
     */
    skipNoticeLevelSettings?: boolean;
  }
): Promise<AffidavitReconciliationSettings | undefined> => {
  const newspaper = await getOrThrow(noticeData.newspaper);
  const newspaperLevelAffidavitSettings =
    await getAffidavitSettingsForNewspaper(newspaper);

  // If the newspaper doesn't do anything with verification, don't do anything!
  if (!newspaperLevelAffidavitSettings) return;

  // Allow for notice-specific settings for affidavit reconciliation
  const noticeLevelAffidavitSettings =
    noticeData.affidavitReconciliationSettings || {};

  // The customer- and customer-organization-level overrides below come from
  // `list` queries that advertisers aren't permitted to run (see firestore.rules:
  // customers / customerOrganizations `allow list`). A permission-denied there
  // must not discard the newspaper/parent settings we've already resolved, so
  // treat it as "no customer-level override" and continue the cascade. On the
  // backend (admin SDK) permission-denied never occurs, so this is a no-op there.
  // Any other error still propagates.

  // Allow for customer-specific settings to override newspaper settings for affidavit reconciliation
  let customerLevelAffidavitSettings = {};
  const filer = await noticeData.filer?.get();
  if (exists(filer)) {
    try {
      const customerSnap = await getCustomer(ctx, filer, newspaper);
      customerLevelAffidavitSettings =
        customerSnap?.data().affidavitReconciliationSettings || {};
    } catch (err) {
      if (!isPermissionDeniedError(err)) {
        throw err;
      }
      getErrorReporter().logWarn(
        'Permission denied fetching affidavit reconciliation settings',
        {
          newspaperRef: newspaper?.ref?.path || 'undefined',
          filerRef: filer?.ref?.path || 'undefined',
          filedByRef: noticeData.filedBy?.path || 'undefined',
          previousNoticeType:
            String(noticeData.previousNoticeType) ?? 'undefined'
        }
      );
    }
  }

  // Allow for customer organization-specific settings to override newspaper settings for affidavit reconciliation
  let customerOrganizationLevelAffidavitSettings = {};
  const filerOrg = await noticeData.filedBy?.get();
  if (exists(filerOrg)) {
    try {
      const customerSnap = await getCustomerOrganization(
        ctx,
        filerOrg,
        newspaper
      );
      customerOrganizationLevelAffidavitSettings =
        customerSnap?.data().affidavitReconciliationSettings || {};
    } catch (err) {
      if (!isPermissionDeniedError(err)) {
        throw err;
      }
    }
  }

  // Allow for notice-type-specific settings
  const noticeType = await getNoticeTypeFromNoticeData(noticeData, newspaper, {
    skipDisplayType: true
  });
  const noticeTypeLevelAffidavitSettings =
    noticeType?.affidavitReconciliationSettings || {};

  return {
    ...newspaperLevelAffidavitSettings,
    ...noticeTypeLevelAffidavitSettings,
    ...customerOrganizationLevelAffidavitSettings,
    ...customerLevelAffidavitSettings,
    ...(options?.skipNoticeLevelSettings ? {} : noticeLevelAffidavitSettings)
  };
};

/**
 * Returns the reconciliation settings affiliated with the notice. Goes in precendence
 * order of:
 * 1. What is set on the notice (if anything)
 * 2. What is set on the customer (if anything)
 * 3. What is set on the notice type (if anything)
 * 4. What is set on the publisher
 */
export const getAffidavitSettingsForNotice = async (
  ctx: EFirebaseContext,
  notice: ESnapshotExists<AAPendingQueryNotice>,
  options?: {
    skipNoticeLevelSettings: boolean;
  }
): Promise<AffidavitReconciliationSettings | undefined> => {
  return getAffidavitSettingsForNoticeData(ctx, notice.data(), options);
};

/**
 * Notices may have an affidavit fee as well as per-mailed-affidavit fees.
 */
export const getAffidavitPricingData = async (
  ctx: EFirebaseContext,
  notice: ESnapshotExists<ENotice>
): Promise<AffidavitPricingData> => {
  const settings = await getAffidavitSettingsForNotice(ctx, notice);
  // Resolved without the notice-level override so add-on pricing can tell a
  // paper that mandates wet sig from a customer who opted into it — electing
  // wet sig stamps `notarizationVendor: 'manual'` onto the notice itself.
  const paperLevelSettings = await getAffidavitSettingsForNotice(ctx, notice, {
    skipNoticeLevelSettings: true
  });
  const mail = await getMailDataFromNoticeOrDraft(notice.ref);
  const noticeModel = getModelFromSnapshot(UserNoticeModel, ctx, notice);
  const requiresAffidavit = await noticeModel.areAffidavitsEnabled();

  return {
    settings,
    paperLevelSettings,
    mail,
    requiresAffidavit,
    noticeAddOnQuote: notice.data().noticeAddOnQuote
  };
};

const getAffidavitFeeIsNumber = (
  affidavitFeeInCents: number | undefined
): affidavitFeeInCents is number => {
  return (
    typeof affidavitFeeInCents === 'number' &&
    !Number.isNaN(affidavitFeeInCents)
  );
};

export const applyAffidavitFeeSplitToColumnAffidavitFeeInCents = (
  grossColumnAffidavitFeeInCents: number,
  feeSplit: FeeSplit | undefined
) => {
  if (!feeSplit || !feeSplit.type) {
    return {
      columnAffidavitFeeInCents: grossColumnAffidavitFeeInCents,
      feeSplit: 0
    };
  }

  const splitType = feeSplit.type;
  if (isFeeSplitTypeFullWaiver(feeSplit)) {
    return {
      columnAffidavitFeeInCents: 0,
      feeSplit: 0,
      amountWaived: grossColumnAffidavitFeeInCents
    };
  }

  const splitAmount = feeSplit.amount;

  if (splitAmount && typeof splitAmount !== 'number') {
    throw new Error(
      `Invalid split amount type: feeSplit is a ${typeof splitAmount} and should be a number`
    );
  }

  if (splitType === 'flat') {
    const columnAffidavitFeeInCents = Math.max(
      0,
      grossColumnAffidavitFeeInCents - splitAmount
    );
    return {
      columnAffidavitFeeInCents,
      feeSplit: grossColumnAffidavitFeeInCents - columnAffidavitFeeInCents
    };
  }

  if (splitType === 'percent') {
    const splitCap = feeSplit.cap;
    const splitAmountInCents = Math.round(
      grossColumnAffidavitFeeInCents * (splitAmount / 100)
    );
    if (splitCap && splitAmountInCents > splitCap) {
      const columnAffidavitFeeInCents = Math.max(
        0,
        grossColumnAffidavitFeeInCents - splitCap
      );
      return {
        columnAffidavitFeeInCents,
        feeSplit: grossColumnAffidavitFeeInCents - columnAffidavitFeeInCents
      };
    }
    const columnAffidavitFeeInCents = Math.max(
      0,
      grossColumnAffidavitFeeInCents - splitAmountInCents
    );
    return {
      columnAffidavitFeeInCents,
      feeSplit: grossColumnAffidavitFeeInCents - columnAffidavitFeeInCents
    };
  }

  return {
    columnAffidavitFeeInCents: grossColumnAffidavitFeeInCents,
    feeSplit: 0
  };
};

const getAffidavitFeeInCentsValue = (
  automatedAffidavitFeeInCents: number | undefined
) => {
  const affidavitFeeIsNumber = getAffidavitFeeIsNumber(
    automatedAffidavitFeeInCents
  );
  const affidavitFeeType = typeof automatedAffidavitFeeInCents;

  if (!affidavitFeeIsNumber && affidavitFeeType !== 'undefined') {
    const unexpectedAffidavitFeeTypeError = new Error(
      'Affidavit fee is of unexpected type'
    );
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.AFFIDAVITS,
      unexpectedAffidavitFeeTypeError,
      'Affidavit fee is of unexpected type (neither number nor undefined) -- cannot price affidavit fee',
      {
        affidavitFeeInCents: automatedAffidavitFeeInCents ?? affidavitFeeType,
        affidavitFeeType
      }
    );
    // TODOBX: To throw an error here, if we can confirm we don't see these crticial errors inproduction
  }

  return affidavitFeeIsNumber
    ? automatedAffidavitFeeInCents
    : DEFAULT_AFFIDAVIT_FEE_IN_CENTS;
};

export const getAffidavitFeeInCentsAndFeeSplitFromSettingsAndMail = (
  reconciliationSettings: Partial<AffidavitReconciliationSettings> | undefined,
  allMail: MailDelivery[]
) => {
  const mail = getBillableMail(allMail);
  const {
    affidavitsManagedByColumn,
    automatedAffidavitFeeInCents,
    notarizationVendor,
    affidavitFeeSplit
  } = reconciliationSettings || {};

  const unitFee = getAffidavitFeeInCentsValue(automatedAffidavitFeeInCents);

  if (affidavitsManagedByColumn) {
    // Wet-sig, RON, and invoice mailings carry their own fees through the
    // noticeAddOns path, so counting them here would double-bill. Standard
    // affidavits have no add-on fee (only add-on shipping); their Column fee is
    // still this legacy automatedAffidavitFeeInCents, so they (and untyped legacy
    // records) must continue to be counted.
    const legacyAffidavitMail = (mail || []).filter(
      m =>
        !m.documentType ||
        m.documentType === MailedNoticeDocumentType.STANDARD_AFFIDAVIT
    );

    // Wet-sig and RON records carry their own per-copy add-on fee, but the notice
    // still gets one Column affidavit. Without this the legacy count is zero on a
    // wet-sig paper (every record is typed WET_SIG_AFFIDAVIT), silently dropping
    // the whole Column affidavit fee from the invoice.
    const hasAddOnAffidavitMail = (mail || []).some(
      m =>
        m.documentType === MailedNoticeDocumentType.WET_SIG_AFFIDAVIT ||
        m.documentType === MailedNoticeDocumentType.RON_AFFIDAVIT
    );

    // If we are manually sending affidavits, we charge per copy. Otherwise just charge once!
    const legacyCopies = legacyAffidavitMail.reduce(
      (a, m) => a + (m.copies || 0),
      0
    );
    const unitQuantity =
      notarizationVendor === 'manual'
        ? legacyCopies || (hasAddOnAffidavitMail ? 1 : 0)
        : 1;

    // apply feeSplit if exists to column affidavit fee in cents
    let finalColumnAffidavitUnitFeeInCents = unitFee;
    let feeSplitAmountInCents = 0;
    let amountWaived;

    if (affidavitFeeSplit?.feeSplit && affidavitFeeSplit?.feeSplit.type) {
      const affidavitFeeWithFeeSplitAppliedResult =
        applyAffidavitFeeSplitToColumnAffidavitFeeInCents(
          unitFee,
          affidavitFeeSplit?.feeSplit
        );

      finalColumnAffidavitUnitFeeInCents =
        affidavitFeeWithFeeSplitAppliedResult.columnAffidavitFeeInCents;
      feeSplitAmountInCents = affidavitFeeWithFeeSplitAppliedResult.feeSplit;
      amountWaived = affidavitFeeWithFeeSplitAppliedResult.amountWaived;
    }

    const invoiceTrackedAffidavitFeeSplitResult = affidavitFeeSplit?.feeSplit
      ? {
          feeSplit: affidavitFeeSplit?.feeSplit,
          amountInCents: feeSplitAmountInCents,
          ...(amountWaived && { amountWaived })
        }
      : undefined;

    return {
      affidavitFeeInCents: unitQuantity * finalColumnAffidavitUnitFeeInCents,
      invoiceTrackedAffidavitFeeSplitResult
    };
  }

  return {
    affidavitFeeInCents: 0,
    invoiceTrackedAffidavitFeeSplitResult: undefined
  };
};

export const getAffidavitFeeInCentsFromSettingsAndMail = (
  reconciliationSettings: Partial<AffidavitReconciliationSettings> | undefined,
  mail: MailDelivery[]
): number => {
  const { affidavitFeeInCents } =
    getAffidavitFeeInCentsAndFeeSplitFromSettingsAndMail(
      reconciliationSettings,
      mail
    );
  return affidavitFeeInCents;
};

export const getAffidavitFeeInCentsForNoticeData = async (
  ctx: EFirebaseContext,
  noticeData: NoticeWithAffidavitData,
  mail: MailDelivery[]
): Promise<number> => {
  const affidavitSettings = await getAffidavitSettingsForNoticeData(
    ctx,
    noticeData
  );
  return getAffidavitFeeInCentsFromSettingsAndMail(affidavitSettings, mail);
};

export const getAffidavitFeeInCentsForNotice = async (
  ctx: EFirebaseContext,
  noticeSnap: ESnapshotExists<ENotice>
): Promise<number> => {
  const mail = await getMailDataFromNoticeOrDraft(noticeSnap.ref);
  return getAffidavitFeeInCentsForNoticeData(ctx, noticeSnap.data(), mail);
};

export const getHasAffidavitServiceStartedByLastPubDate = (
  reconciliationStartDate: FirebaseTimestamp,
  noticePublicationDates: ENotice['publicationDates']
) => {
  const sortedPublicationDates = sortNoticePublicationDatesChronologically(
    noticePublicationDates
  );
  const lastPublicationDate =
    sortedPublicationDates[sortedPublicationDates.length - 1].toDate();

  return moment(lastPublicationDate)
    .startOf('day')
    .isSameOrAfter(moment(reconciliationStartDate.toDate()).startOf('day'));
};

export const getShouldApplyColumnManagedAffidavitFees = (
  affidavitReconciliationSettings: AffidavitReconciliationSettings | undefined,
  noticePublicationDates: ENotice['publicationDates'] | undefined,
  requiresAffidavit: boolean
) => {
  if (!affidavitReconciliationSettings) {
    return false;
  }

  if (!affidavitReconciliationSettings.affidavitsManagedByColumn) {
    return false;
  }

  /**
   * TODO(column-pro): This is a bad settings configuration; I'm only logging it for now, but we may want to add a roadmap item for preventing this from happening in the future
   */
  if (!requiresAffidavit) {
    getErrorReporter().logWarn(
      'Notice has affidavits managed by Column but does not require affidavits',
      {
        service: ColumnService.AFFIDAVITS,
        managedAffidavitTemplateId:
          affidavitReconciliationSettings.managedAffidavitTemplate?.id ?? 'N/A',
        managedAffidavitTemplateStoragePath:
          affidavitReconciliationSettings.managedAffidavitTemplateStoragePath ??
          'N/A'
      }
    );
    return false;
  }

  if (!noticePublicationDates?.length) {
    return false;
  }

  const { reconciliationStartDate } = affidavitReconciliationSettings;

  /* If we have ARS but don't specific a reconciliationStartDate, we assume the fee applies to
  new invoices created regardless of last publication date */

  if (!reconciliationStartDate || !reconciliationStartDate.toDate()) {
    return true;
  }

  return getHasAffidavitServiceStartedByLastPubDate(
    reconciliationStartDate,
    noticePublicationDates
  );
};

/**
 * Returns true when a notice add-on is already paying for the notice's affidavit,
 * meaning the legacy per-notice automatedAffidavitFeeInCents must be 0 — charging
 * both would double-bill the same affidavit.
 *
 * The distinction is whether the add-on covers the *first* (included) affidavit or
 * only the extra copies:
 *  - Wet-sig-required paper: the publisher covers the first copy, so the add-on
 *    prices extras only and the Column affidavit fee still applies.
 *  - RON paper where the customer elects wet sig: the add-on is charged from the
 *    first copy, so it replaces the Column affidavit fee.
 */
export const noticeAddOnCoversAffidavit = (
  noticeAddOns: NoticeAddOnPricing | null | undefined,
  context: NoticeAddOnPricingContext | undefined
): boolean => {
  if (noticeAddOns?.ronAffidavit) {
    return true;
  }
  if (!noticeAddOns?.wetSigAffidavit) {
    return false;
  }
  return !context?.firstWetSigAffidavitFree;
};
